import React, { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { MapPin, ArrowLeft, Edit, Users, Building2, User, Calendar, Loader2, Plus, Trash2, Info, ExternalLink } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { areasService } from '@/services/areas'
import { useAuth } from '@/context/AuthContext'
import { NavigationUtils } from '@/utils/navigation'
import toast from 'react-hot-toast'

const AreaDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { hasRole } = useAuth()
  const queryClient = useQueryClient()

  const [currentUsersPage, setCurrentUsersPage] = useState(1)
  const [usersPageSize] = useState(10)

  // Fetch area details
  const {
    data: area,
    isLoading: areaLoading,
    error: areaError
  } = useQuery(
    ['area', id],
    () => areasService.getById(id),
    {
      onError: (error) => {
        if (error.response?.status === 404) {
          toast.error('Area not found')
          NavigationUtils.navigateBackToSettings(navigate, 'areas')
        } else {
          toast.error('Failed to load area details')
        }
      }
    }
  )

  // Fetch area users
  const {
    data: areaUsersData,
    isLoading: usersLoading,
    error: usersError
  } = useQuery(
    ['area-users', id, currentUsersPage, usersPageSize],
    () => areasService.getAreaUsers(id, {
      page: currentUsersPage,
      page_size: usersPageSize
    }),
    {
      enabled: !!id,
      onError: (error) => {
        console.error('Error loading area users:', error)
      }
    }
  )

  // Remove user mutation
  const removeUserMutation = useMutation(
    ({ areaId, userId }) => areasService.removeUser(areaId, userId),
    {
      onSuccess: () => {
        toast.success('User removed from area successfully')
        queryClient.invalidateQueries(['area-users', id])
        queryClient.invalidateQueries(['area', id])
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to remove user from area')
      }
    }
  )

  // Handle remove user
  const handleRemoveUser = (userId, userName) => {
    if (window.confirm(`Are you sure you want to remove ${userName} from this area?`)) {
      removeUserMutation.mutate({ areaId: id, userId })
    }
  }

  // Check permissions
  const canEditArea = hasRole(['admin', 'manager'])
  const canManageUsers = hasRole(['admin', 'manager'])

  if (areaLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mr-3" />
          <span className="text-gray-600">Loading area details...</span>
        </div>
      </div>
    )
  }

  if (areaError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Area</h2>
          <p className="text-gray-600 mb-4">There was a problem loading the area details.</p>
          <button
            onClick={() => NavigationUtils.navigateBackToSettings(navigate, 'areas')}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Back to Areas
          </button>
        </div>
      </div>
    )
  }

  const areaUsers = areaUsersData?.user_areas || []
  const totalUsers = areaUsersData?.total_count || 0
  const totalUsersPages = areaUsersData?.total_pages || 1

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => NavigationUtils.navigateBackToSettings(navigate, 'areas')}
            className="btn-outline btn-sm flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Areas
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{area?.name}</h1>
            <p className="mt-1 text-sm text-gray-600">Area details and user management</p>
          </div>
        </div>
        <div>
          {canEditArea && (
            <Link
              to={`/areas/${id}/edit`}
              className="btn-primary btn-md flex items-center"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit Area
            </Link>
          )}
        </div>
      </div>

      {/* User Assignment Guidance */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <Info className="h-6 w-6 text-blue-600" />
          </div>
          <div className="ml-4 flex-1">
            <h3 className="text-lg font-medium text-blue-900 mb-2">
              Managing Area Assignments
            </h3>
            <div className="text-blue-800 space-y-3">
              <p className="text-sm">
                Area managers and users are assigned through the <strong>User Management</strong> section.
                This provides centralized control over user permissions and area access.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="bg-white rounded-md p-4 border border-blue-200">
                  <h4 className="font-medium text-blue-900 mb-2 flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    Assign Area Manager
                  </h4>
                  <p className="text-sm text-blue-700 mb-3">
                    To assign an area manager to this area:
                  </p>
                  <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                    <li>Go to User Management</li>
                    <li>Create or edit a user with "Area Manager" role</li>
                    <li>Select this area in their area access</li>
                    <li>Save - they'll become the area manager</li>
                  </ol>
                  <Link
                    to="/settings?tab=users"
                    className="inline-flex items-center mt-3 text-sm font-medium text-blue-600 hover:text-blue-800"
                  >
                    Go to User Management
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </Link>
                </div>

                <div className="bg-white rounded-md p-4 border border-blue-200">
                  <h4 className="font-medium text-blue-900 mb-2 flex items-center">
                    <Users className="h-4 w-4 mr-2" />
                    Assign Regular Users
                  </h4>
                  <p className="text-sm text-blue-700 mb-3">
                    To assign regular users to this area:
                  </p>
                  <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                    <li>Go to User Management</li>
                    <li>Create or edit a user with "User" role</li>
                    <li>Select this area in their area access</li>
                    <li>Save - they'll have access to this area</li>
                  </ol>
                  <Link
                    to="/settings?tab=users"
                    className="inline-flex items-center mt-3 text-sm font-medium text-blue-600 hover:text-blue-800"
                  >
                    Go to User Management
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </Link>
                </div>
              </div>

              <div className="mt-4 p-3 bg-blue-100 rounded-md">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Each area can only have one area manager, but multiple regular users.
                  If you try to assign an area manager to an area that already has one, the new user will be assigned as a regular user instead.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Area Information */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Area Information</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Name</label>
                  <p className="mt-1 text-sm text-gray-900">{area?.name}</p>
                </div>

                {area?.description && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Description</label>
                    <p className="mt-1 text-sm text-gray-900">{area.description}</p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-500">Brand</label>
                  <div className="mt-1 flex items-center">
                    <Building2 className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">{area?.brand_name || 'N/A'}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500">Manager</label>
                  <div className="mt-1 flex items-center">
                    <User className="h-4 w-4 text-gray-400 mr-2" />
                    <div>
                      <span className="text-sm text-gray-900">
                        {area?.manager_name || 'No manager assigned'}
                      </span>
                      {area?.manager_email && (
                        <p className="text-xs text-gray-500">{area.manager_email}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500">Status</label>
                  <div className="mt-1">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      area?.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {area?.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500">Total Users</label>
                  <div className="mt-1 flex items-center">
                    <Users className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">{area?.user_count || 0}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500">Created</label>
                  <div className="mt-1 flex items-center">
                    <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-900">
                      {area?.created_at ? new Date(area.created_at).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Users in Area */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Users in Area</h2>
                  {canManageUsers && (
                    <Link
                      to={`/areas/${id}/assign-users`}
                      className="bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 flex items-center space-x-1 text-sm"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Assign Users</span>
                    </Link>
                  )}
                </div>
              </div>

              <div className="p-6">
                {usersLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-600 mr-2" />
                    <span className="text-gray-600">Loading users...</span>
                  </div>
                ) : areaUsers.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No users assigned</h3>
                    <p className="text-gray-600 mb-4">
                      This area doesn't have any users assigned yet.
                    </p>
                    {canManageUsers && (
                      <Link
                        to={`/areas/${id}/assign-users`}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 inline-flex items-center space-x-2"
                      >
                        <Plus className="h-4 w-4" />
                        <span>Assign Users</span>
                      </Link>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="space-y-3">
                      {areaUsers.map((userArea) => (
                        <div
                          key={userArea.id}
                          className="flex items-center justify-between p-3 border border-gray-200 rounded-md hover:bg-gray-50"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <User className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {userArea.user_name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {userArea.user_email}
                              </p>
                            </div>
                          </div>
                          {canManageUsers && (
                            <button
                              onClick={() => handleRemoveUser(userArea.user_id, userArea.user_name)}
                              disabled={removeUserMutation.isLoading}
                              className="text-red-600 hover:text-red-900 p-1"
                              title="Remove from area"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Users Pagination */}
                    {totalUsersPages > 1 && (
                      <div className="mt-6 flex items-center justify-between">
                        <div className="text-sm text-gray-700">
                          Showing {(currentUsersPage - 1) * usersPageSize + 1} to{' '}
                          {Math.min(currentUsersPage * usersPageSize, totalUsers)} of {totalUsers} users
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setCurrentUsersPage(prev => Math.max(1, prev - 1))}
                            disabled={currentUsersPage === 1}
                            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            Previous
                          </button>
                          <span className="px-3 py-1 text-sm text-gray-700">
                            Page {currentUsersPage} of {totalUsersPages}
                          </span>
                          <button
                            onClick={() => setCurrentUsersPage(prev => Math.min(totalUsersPages, prev + 1))}
                            disabled={currentUsersPage === totalUsersPages}
                            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
    </div>
  )
}

export default AreaDetails
